import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

export async function getExperts() {
  const { data: expertsData, error: expertsError } = await supabase
    .from('experts')
    .select('*')
    .order('rating', { ascending: false });

  if (expertsError) throw expertsError;

  // Fetch reviews and user profiles for each expert
  const transformedData = await Promise.all(
    expertsData.map(async (expert) => {
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('expert_reviews')
        .select(`
          id,
          rating,
          comment,
          created_at,
          user_id
        `)
        .eq('expert_id', expert.id);

      if (reviewsError) throw reviewsError;

      // For each review, fetch the user profile
      const reviewsWithUsers = await Promise.all(
        reviewsData.map(async (review) => {
          const { data: userData, error: userError } = await supabase
            .from('user_profiles')
            .select('user_id, full_name')
            .eq('user_id', review.user_id)
            .single();

          if (userError && userError.code !== 'PGRST116') throw userError;

          return {
            ...review,
            user: {
              id: review.user_id,
              full_name: userData?.full_name || 'Anonymous User'
            }
          };
        })
      );

      return {
        ...expert,
        reviews: reviewsWithUsers
      };
    })
  );

  return transformedData;
}

export async function getExpertById(id: string) {
  const { data: expert, error: expertError } = await supabase
    .from('experts')
    .select('*')
    .eq('id', id)
    .single();

  if (expertError) throw expertError;

  const { data: reviewsData, error: reviewsError } = await supabase
    .from('expert_reviews')
    .select(`
      id,
      rating,
      comment,
      created_at,
      user_id
    `)
    .eq('expert_id', id);

  if (reviewsError) throw reviewsError;

  // Fetch user profiles for each review
  const reviewsWithUsers = await Promise.all(
    reviewsData.map(async (review) => {
      const { data: userData, error: userError } = await supabase
        .from('user_profiles')
        .select('user_id, full_name')
        .eq('user_id', review.user_id)
        .single();

      if (userError && userError.code !== 'PGRST116') throw userError;

      return {
        ...review,
        user: {
          id: review.user_id,
          full_name: userData?.full_name || 'Anonymous User'
        }
      };
    })
  );

  return {
    ...expert,
    reviews: reviewsWithUsers
  };
}

export async function bookConsultation(expertId: string, scheduledAt: Date, type: 'video' | 'chat') {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('expert_consultations')
    .insert([
      {
        expert_id: expertId,
        user_id: user.id,
        scheduled_at: scheduledAt.toISOString(),
        duration: '1 hour',
        status: 'scheduled',
        type
      }
    ])
    .select()
    .single();

  if (error) throw error;

  // Send confirmation email
  await sendConsultationConfirmationEmail(user.email!, data);

  return data;
}

export async function submitReview(expertId: string, rating: number, comment: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('expert_reviews')
    .insert([
      {
        expert_id: expertId,
        user_id: user.id,
        rating,
        comment
      }
    ])
    .select()
    .single();

  if (error) throw error;

  // Get user profile
  const { data: userData, error: userError } = await supabase
    .from('user_profiles')
    .select('user_id, full_name')
    .eq('user_id', user.id)
    .single();

  if (userError && userError.code !== 'PGRST116') throw userError;

  // Update expert's average rating
  await updateExpertRating(expertId);

  return {
    ...data,
    user: {
      id: user.id,
      full_name: userData?.full_name || 'Anonymous User'
    }
  };
}

async function updateExpertRating(expertId: string) {
  const { data, error } = await supabase
    .from('expert_reviews')
    .select('rating')
    .eq('expert_id', expertId);

  if (error) throw error;

  const avgRating = data.reduce((acc, review) => acc + review.rating, 0) / data.length;

  await supabase
    .from('experts')
    .update({ rating: avgRating })
    .eq('id', expertId);
}

async function sendConsultationConfirmationEmail(email: string, consultation: any) {
  await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      to: email,
      subject: 'Consultation Scheduled',
      content: `
        <h1>Consultation Scheduled</h1>
        <p>Your ${consultation.type} consultation has been scheduled for ${new Date(consultation.scheduled_at).toLocaleString()}.</p>
        <p>Duration: ${consultation.duration}</p>
        <p>Type: ${consultation.type}</p>
      `
    }),
  });
}